import React, { memo, useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { NetworkStatus } from '@apollo/client';
import { map, isEmpty, get, find, isNil } from 'lodash';
import { BarChart, Bar, Legend, XAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { FR_GREY_200, FR_PR_BLUE, FR_SP3 } from '@shared/styles/vars';
import SectionTitle from '@src/components/SectionTitle';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { numberWithCommas } from '@src/util/numberWithCommas';
import { useOpeningMarketSalariesQueryQuery } from '@src/graphql/generated';
import './styles.scss';

const MIN_OPENINGS_SAMPLE_SIZE = 10;
const BAR_WIDTH = 16;
const X_AXSIS_INTERVAL = 1;
const OFFERED_SALARY = {
  COLOR: FR_SP3,
  DATA_KEY: 'offeredPercentage',
  NAME: 'Offered',
};
const EXPECTED_SALARY = {
  COLOR: FR_PR_BLUE,
  DATA_KEY: 'expectedPercentage',
  NAME: 'Expected',
};
const DEFAULT_TOOLTIP_DATA = { activeIndex: -1, activeValue: null, position: { x: 0, y: 0 } };

const _SalariesChart = memo(({ openingId, updatedAt, from, to, selectSalaryRange, setErrorFlashMessage }) => {
  const [{ activeIndex, position, activeValue }, setTooltipData] = useState(DEFAULT_TOOLTIP_DATA);
  const [{ selecting, ...selected }, setSelected] = useState({ from, selecting: false, startedPoint: null, to });

  const { data, error, refetch, networkStatus } = useOpeningMarketSalariesQueryQuery({
    notifyOnNetworkStatusChange: true,
    onError: () => setErrorFlashMessage(),
    variables: { id: openingId },
  });

  const marketSalaries = get(data, 'opening.marketSalaries');
  const refetchLoading = networkStatus === NetworkStatus.refetch;

  useEffect(() => {
    refetch();
    setTooltipData(DEFAULT_TOOLTIP_DATA);
  }, [updatedAt]);

  useEffect(() => {
    setSelected(prevState => ({ ...prevState, from, to }));
  }, [from, to]);

  const handleBarMouseOver = ({ x, y, value: activeValue }, activeIndex) => {
    setTooltipData({
      activeIndex,
      activeValue,
      position: { x: x + BAR_WIDTH / 2, y },
    });
  };

  const userSalaries = get(marketSalaries, 'userSalaries') || [];

  if (!marketSalaries || isEmpty(userSalaries) || error) {
    return null;
  }

  const { currency, openingSalaries } = marketSalaries;
  const openingsSampleSize = !isEmpty(openingSalaries) ? openingSalaries.reduce((acc, { count }) => acc + count, 0) : 0;
  const offeredIsShown = openingsSampleSize > MIN_OPENINGS_SAMPLE_SIZE;
  const userIsInteracting = activeIndex >= 0;
  const noBarActive = !selected.from && !selected.to && !userIsInteracting;

  const stepWidth = Math.round((userSalaries[1].step - userSalaries[0].step) / 2);
  const chartData = map(userSalaries, ({ percentage, step }, i) => ({
    active: (!isNil(selected.from) && step >= selected.from && step <= selected.to) || i === activeIndex,
    [OFFERED_SALARY.DATA_KEY]: offeredIsShown ? openingSalaries[i].percentage : 0,
    [EXPECTED_SALARY.DATA_KEY]: percentage,
    step,
  }));

  const cellList = chartData.map(({ active }, i) => (
    <Cell cursor="pointer" key={i} opacity={active || noBarActive ? 1 : 0.5} width={BAR_WIDTH} />
  ));

  return (
    <Fragment>
      <SectionTitle
        subtitle="Based on your selections, this is what people are earning. Click and drag to select a range."
        title="Your target group full-time salaries (gross)"
      />

      <div className={classnames('salaries-chart', { 'salaries-chart--loading': refetchLoading })}>
        <ResponsiveContainer debounce={400} height={264}>
          <BarChart
            data={chartData}
            onMouseDown={data =>
              setSelected({
                from: get(data, 'activeLabel', null),
                selecting: true,
                startedPoint: get(data, 'activeLabel', null),
                to: get(data, 'activeLabel', null),
              })
            }
            onMouseLeave={() => {
              setSelected(prevState => {
                return { ...prevState, selecting: false, startedPoint: null };
              });
              setTooltipData(DEFAULT_TOOLTIP_DATA);
            }}
            onMouseMove={data => {
              if (activeIndex >= 0 && data && data.activeTooltipIndex !== activeIndex) {
                setTooltipData(DEFAULT_TOOLTIP_DATA);
              }

              selecting &&
                setSelected(prevState => {
                  const lastSelectedValue = get(data, 'activeLabel', null);

                  return prevState.startedPoint < lastSelectedValue
                    ? { ...prevState, from: prevState.startedPoint, to: lastSelectedValue }
                    : { ...prevState, from: lastSelectedValue, to: prevState.startedPoint };
                });
            }}
            onMouseUp={() =>
              setSelected(prevState => {
                const getNewSalaryRange = (prevFrom, prevTo) => ({
                  from: prevFrom - stepWidth,
                  to: prevTo + stepWidth,
                });

                if (prevState.to && prevState.from !== prevState.to) {
                  const newSalaryRange = getNewSalaryRange(prevState.from, prevState.to);

                  selectSalaryRange(newSalaryRange);

                  return {
                    ...prevState,
                    ...newSalaryRange,
                    selecting: false,
                    startedPoint: null,
                  };
                } else {
                  const newSalaryRange = getNewSalaryRange(from, to);

                  return {
                    ...newSalaryRange,
                    selecting: false,
                  };
                }
              })
            } // fixes the issue when bars remained active after hovering a cursor quickly
          >
            {offeredIsShown && (
              <Legend
                align="right"
                formatter={value => <span className="salaries-chart__axis-item">{value}</span>}
                height={0}
                iconSize={8}
                iconType="circle"
                verticalAlign="top"
                wrapperStyle={{ fontSize: '1.1rem', lineHeight: '1.6rem', right: '0.8rem', top: '0', width: 'auto' }}
              />
            )}

            <CartesianGrid stroke={FR_GREY_200} vertical={false} />

            <XAxis
              axisLine={false}
              dataKey="step"
              interval={X_AXSIS_INTERVAL}
              tick={({ x, y, payload }) => (
                <g transform={`translate(${x}, ${y + 8})`}>
                  <text className="salaries-chart__axis-item" textAnchor="middle">
                    {currency.symbol}
                    {numberWithCommas(payload.value)}
                  </text>
                </g>
              )}
              tickLine={false}
              tickMargin={16}
            />

            {offeredIsShown ? (
              <Bar
                dataKey={OFFERED_SALARY.DATA_KEY}
                fill={OFFERED_SALARY.COLOR}
                name={OFFERED_SALARY.NAME}
                onMouseEnter={handleBarMouseOver}
              >
                {cellList}
              </Bar>
            ) : null}

            <Bar
              dataKey={EXPECTED_SALARY.DATA_KEY}
              fill={EXPECTED_SALARY.COLOR}
              name={EXPECTED_SALARY.NAME}
              onMouseEnter={handleBarMouseOver}
            >
              {cellList}
            </Bar>

            {userIsInteracting && (
              <Tooltip
                content={({ payload }) =>
                  !isEmpty(payload) ? (
                    <div className="salaries-chart__tooltip-wrapper">
                      <div className="salaries-chart__tooltip">
                        <span className="salaries-chart__tooltip-title">
                          {currency.symbol}
                          {numberWithCommas(payload[0].payload.step - stepWidth)}-{currency.symbol}
                          {numberWithCommas(payload[0].payload.step + stepWidth)}
                        </span>
                        <span className="salaries-chart__tooltip-text">
                          {get(find(payload, ['value', activeValue]), 'value', 0).toFixed(1)}%
                        </span>

                        <div className="salaries-chart__tooltip-arrow">
                          <div className="salaries-chart__tooltip-angle" />
                        </div>
                      </div>
                    </div>
                  ) : null
                }
                cursor={false}
                isAnimationActive={false}
                position={position}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Fragment>
  );
});

_SalariesChart.propTypes = {
  from: PropTypes.number,
  openingId: PropTypes.string.isRequired,
  selectSalaryRange: PropTypes.func.isRequired,
  setErrorFlashMessage: PropTypes.func.isRequired,
  to: PropTypes.number,
  updatedAt: PropTypes.string,
};
_SalariesChart.displayName = 'SalariesChart';

export const SalariesChart = connect(null, { setErrorFlashMessage })(_SalariesChart);
